import type { ReactNode } from "react";
import AyudaVista, { type ContenidoAyuda } from "@/ui/AyudaVista";

/**
 * Marco de una sección de Configuración: encabezado con su explicación y el
 * cuerpo.
 *
 * La explicación vive AQUÍ, una sola vez y arriba, en vez de repartida en
 * párrafos entre los campos como estaba en el panel de Sistema. El encabezado
 * explica; los campos no vuelven a explicarse.
 */
export default function SeccionLayout({
  titulo,
  descripcion,
  ayuda,
  ancho = "completo",
  children,
}: {
  titulo: string;
  descripcion: string;
  /** Contenido del botón «Ayuda» de la sección (qué hace cada botón). Vive en
   *  `components/ayuda/vistas/configuracion.tsx`, por el id de la sección. */
  ayuda?: ContenidoAyuda;
  /** "formulario" limita el ancho: una línea de 1,700px no se lee. */
  ancho?: "formulario" | "completo";
  children: ReactNode;
}) {
  return (
    <section className={`space-y-4 ${ancho === "formulario" ? "max-w-3xl" : ""}`}>
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-medium text-zinc-100">{titulo}</h2>
          {ayuda && <AyudaVista titulo={titulo} {...ayuda} />}
        </div>
        <p className="max-w-2xl text-xs text-zinc-500">{descripcion}</p>
      </header>
      {children}
    </section>
  );
}

/**
 * Pie de guardado de una sección.
 *
 * Va pegado a lo que guarda, no al final de la pantalla: el botón viejo de
 * "Guardar configuración" quedaba a ~1,900px de scroll de la mitad de los
 * campos que afectaba, y había que aclarar con letras cuáles se aplicaban con
 * él. Con el botón aquí, esa aclaración sobra.
 *
 * Los switches de encendido/apagado NO pasan por este pie: guardan solos al
 * instante con su propio aviso.
 */
export function PieGuardar({
  sucio,
  guardando,
  error,
  onGuardar,
  textoGuardar = "Guardar cambios",
}: {
  sucio: boolean;
  guardando: boolean;
  /** Si hay error de validación, se muestra aquí y el botón queda apagado. */
  error?: string | null;
  onGuardar: () => void;
  textoGuardar?: string;
}) {
  const bloqueado = !sucio || guardando || !!error;
  return (
    <div className="flex flex-col gap-2 border-t border-ink-700 pt-3 sm:flex-row sm:items-center sm:justify-between">
      <p className={`text-xs ${error ? "text-red-300" : "text-zinc-500"}`}>
        {error ?? (sucio ? "Hay cambios sin guardar." : "Todo guardado.")}
      </p>
      <button
        type="button"
        onClick={onGuardar}
        disabled={bloqueado}
        className="btn-primary w-full text-sm disabled:opacity-50 sm:w-auto"
      >
        {guardando ? "Guardando…" : textoGuardar}
      </button>
    </div>
  );
}

/**
 * Bloque con título dentro de una sección, separado por una línea.
 *
 * Para cuando una sección tiene dos cosas que se guardan juntas pero se leen
 * aparte (los umbrales globales y el SLA por estado, por ejemplo). No es una
 * card anidada a propósito: apilar cards dentro de cards engorda el margen sin
 * agregar información.
 */
export function BloqueSeccion({
  titulo,
  descripcion,
  children,
}: {
  titulo: string;
  descripcion?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2 border-t border-ink-700 pt-3 first:border-t-0 first:pt-0">
      <div>
        <h3 className="text-sm font-medium text-zinc-300">{titulo}</h3>
        {descripcion && <p className="mt-1 max-w-2xl text-xs text-zinc-500">{descripcion}</p>}
      </div>
      {children}
    </div>
  );
}
